/**
 * Bridge route for the Parallax SSO deep-link flow.
 *
 * Triggered when Parallax (the external service) detects an unauthenticated
 * request and 302s the browser to:
 *     /ai/auth/parallax-redirect?return=<original parallax path>
 *
 * Behavior:
 *   - If the user is already logged into DatacenterAI, fetch a one-time SSO
 *     URL from the backend and `window.location.replace` to it.
 *   - If not logged in, navigate to /login?next=<bridge URL> so they come
 *     back here after authenticating.
 *
 * Must be a top-level route (NOT wrapped in <ProtectedRoute>): we need to
 * preserve the `return` param when bouncing the user through /login.
 *
 * See docs/features/active/parallax-integration/PARALLAX_SSO_SPEC.md
 */

import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import useAuthStore from '../../../../stores/authStore';
import { authenticatedFetch } from '../../../../utils/apiClient';

export default function ParallaxRedirect() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const returnPath = params.get('return') ?? '/';

  const isInitialized = useAuthStore(s => s.isInitialized);
  const accessToken = useAuthStore(s => s.accessToken);

  useEffect(() => {
    // Wait for Zustand `persist` hydration before judging auth state.
    if (!isInitialized) return;

    if (!accessToken) {
      const here = `/auth/parallax-redirect?return=${encodeURIComponent(returnPath)}`;
      navigate(`/login?next=${encodeURIComponent(here)}`, { replace: true });
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const response = await authenticatedFetch(
          `/api/parallax/sso-link?return_path=${encodeURIComponent(returnPath)}`
        );
        if (cancelled) return;

        if (!response.ok) {
          // 401 is handled inside authenticatedFetch / handleApiResponse.
          // 403 (no parallax permission) and 5xx land here.
          console.error('[parallax-redirect] sso-link failed:', response.status);
          navigate('/', { replace: true });
          return;
        }
        const data = await response.json();
        if (data?.url) {
          window.location.replace(data.url);
        } else {
          navigate('/', { replace: true });
        }
      } catch (err) {
        if (!cancelled) {
          console.error('[parallax-redirect] error:', err);
          navigate('/', { replace: true });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isInitialized, accessToken, returnPath, navigate]);

  return (
    <div
      style={{
        padding: 48,
        textAlign: 'center',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}
    >
      {isInitialized ? 'Redirecting to Parallax…' : 'Loading…'}
    </div>
  );
}
